
import {
    useMemo
}
from "react";


//========================================================
// Home Intelligence Data Hook
//
// Generates realistic demonstration data for:
//
//   - Utilisation
//   - Space
//   - Energy & Utilities
//   - Financials
//   - Carbon
//   - Incidents
//
// Labels remain fixed.
// Values are randomised once per browser session.
//
// Chart selection does not regenerate the data.
//
// All data is DEMONSTRATION DATA.
//========================================================


//========================================================
// Fixed Portfolio Labels
//========================================================

const BUILDINGS = [
    "London HQ",
    "Manchester",
    "Birmingham",
    "Bristol",
    "Leeds",
    "Edinburgh",
    "Cardiff",
    "Glasgow",
    "Liverpool",
    "Newcastle"
];


const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];


const SUBJECT_CONFIG = {

    utilisation: {
        label: "Utilisation",
        unit: "%",
        categories: [
            "Workstations",
            "Meeting Rooms",
            "Collaboration",
            "Focus Spaces",
            "Breakout Areas",
            "Shared Facilities"
        ],
        min: 45,
        max: 90,
        target: 85
    },

    space: {
        label: "Space",
        unit: "m²",
        categories: [
            "Workstations",
            "Meeting Rooms",
            "Collaboration",
            "Circulation",
            "Support",
            "Amenities"
        ],
        min: 400,
        max: 2600
    },

    energy: {
        label: "Energy & Utilities",
        unit: "MWh",
        categories: [
            "Electricity",
            "Gas",
            "Water",
            "Heating",
            "Cooling",
            "Other"
        ],
        min: 80,
        max: 450
    },

    financials: {
        label: "Financials",
        unit: "£",
        categories: [
            "Rent",
            "Service Charges",
            "Utilities",
            "Maintenance",
            "Cleaning",
            "Other"
        ],
        min: 20000,
        max: 150000
    },

    carbon: {
        label: "Carbon",
        unit: "tCO₂e",
        categories: [
            "Electricity",
            "Gas",
            "Transport",
            "Waste",
            "Water",
            "Other"
        ],
        min: 20,
        max: 180
    },

    incidents: {
        label: "Incidents",
        unit: "",
        categories: [
            "Health & Safety",
            "Fire Safety",
            "Security",
            "Maintenance",
            "Environmental",
            "Compliance"
        ],
        min: 2,
        max: 35
    }

};


//========================================================
// Random Number Helpers
//========================================================

function randomBetween(min, max)
{

    return Math.floor(
        Math.random() * (max - min + 1)
    ) + min;

}


function roundTo(value, decimals = 0)
{

    const factor =
        Math.pow(10, decimals);

    return Math.round(value * factor) / factor;

}


//========================================================
// Generate Category Breakdown
//========================================================

function generateCategories(config)
{

    return config.categories.map(
        function(category)
        {

            return {
                category: category,
                value: randomBetween(
                    config.min,
                    config.max
                )
            };

        }
    );

}


//========================================================
// Generate Building Data
//
// Building names remain fixed.
//
// Each building receives a total and a breakdown
// of the six subject categories.
//========================================================

function generateBuildings(config)
{

    return BUILDINGS.map(
        function(building)
        {

            const breakdown =
                generateCategories(config);

            const total =
                breakdown.reduce(
                    function(sum, item)
                    {
                        return sum + item.value;
                    },
                    0
                );

            return {
                category: building,
                value: total,
                breakdown: breakdown
            };

        }
    );

}


//========================================================
// Generate Monthly Trend
//
// A gentle seasonal pattern prevents completely
// chaotic month-to-month movements.
//========================================================

function generateTrend(config)
{

    const midpoint =
        (config.min + config.max) / 2;

    const base =
        midpoint * config.categories.length;

    return MONTHS.map(
        function(month, index)
        {

            const seasonal =
                Math.sin(
                    (index / 12) * Math.PI * 2
                ) * base * 0.08;

            const variation =
                randomBetween(-8, 8) / 100;

            const value =
                base * (1 + variation) + seasonal;

            return {
                category: month,
                value: roundTo(
                    Math.max(0, value),
                    0
                )
            };

        }
    );

}


//========================================================
// Generate Subject Data
//========================================================

function generateSubjectData(subjectId)
{

    const config =
        SUBJECT_CONFIG[subjectId];

    if(!config)
    {
        return null;
    }


    //----------------------------------------------------
    // Category Breakdown
    //----------------------------------------------------

    const categories =
        generateCategories(config);


    //----------------------------------------------------
    // Building Breakdown
    //----------------------------------------------------

    const buildings =
        generateBuildings(config);


    //----------------------------------------------------
    // Monthly Trend
    //----------------------------------------------------

    const trend =
        generateTrend(config);


    //----------------------------------------------------
    // Top Ten Buildings
    //----------------------------------------------------

    const topTen =
        [...buildings].sort(
            function(a, b)
            {
                return b.value - a.value;
            }
        );


    //----------------------------------------------------
    // Overall Total
    //----------------------------------------------------

    const total =
        categories.reduce(
            function(sum, item)
            {
                return sum + item.value;
            },
            0
        );


    //----------------------------------------------------
    // Average
    //----------------------------------------------------

    const average =
        roundTo(
            total / categories.length,
            1
        );


    //----------------------------------------------------
    // Return Unified Dataset
    //----------------------------------------------------

    return {

        id: subjectId,

        label: config.label,

        unit: config.unit,

        target: config.target || null,

        total: total,

        average: average,

        categories: categories,

        buildings: buildings,

        trend: trend,

        topTen: topTen,

        spider: categories,

        funnel: [...categories].sort(
            function(a, b)
            {
                return b.value - a.value;
            }
        ),

        isDemo: true

    };

}


//========================================================
// Generate Complete Portfolio Dataset
//========================================================

function generatePortfolioData()
{

    const result = {};

    Object.keys(SUBJECT_CONFIG).forEach(
        function(subjectId)
        {

            result[subjectId] =
                generateSubjectData(subjectId);

        }
    );

    return result;

}


//========================================================
// Session Data Cache
//
// Module-level cache ensures all cards share the
// same demonstration portfolio data.
//
// Reloading the page generates a fresh portfolio.
//========================================================

let cachedPortfolioData = null;


function getPortfolioData()
{

    if(!cachedPortfolioData)
    {

        cachedPortfolioData =
            generatePortfolioData();

    }

    return cachedPortfolioData;

}


//========================================================
// React Hook
//========================================================

export default function useHomeIntelligenceData(
    subjectId,
    chartType = "Total"
)
{

    //----------------------------------------------------
    // Stable Subject Data
    //----------------------------------------------------

    const data =
        useMemo(
            function()
            {

                const portfolio =
                    getPortfolioData();

                return portfolio[subjectId] || null;

            },
            [subjectId]
        );


    //----------------------------------------------------
    // Select Chart Dataset
    //----------------------------------------------------

    const chartData =
        useMemo(
            function()
            {

                if(!data)
                {
                    return [];
                }

                switch(chartType.toLowerCase())
                {

                    case "total":
                        return data.categories;

                    case "stack":
                        return data.buildings;

                    case "trend":
                        return data.trend;

                    case "top ten":
                    case "topten":
                        return data.topTen;

                    case "spider":
                        return data.spider;

                    case "funnel":
                        return data.funnel;

                    default:
                        return data.categories;

                }

            },
            [data, chartType]
        );


    //----------------------------------------------------
    // Hook Response
    //----------------------------------------------------

    return {

        data: data,

        chartData: chartData,

        loading: false,

        error: null,

        isDemo: true

    };

}
